// Boots the app: session check, sign-in, the AppContext, the top bar, hash routing and keyboard keys.
import type { Deal, DealResponse, Network, PrivateNote, PrivateState } from "../shared/network";
import { holdReason, stateAsOf, todayIso } from "../shared/engine";
import { api, type Session } from "./lib/api";
import type { AppContext, HoldInfo, MountFn, PageId } from "./context";
import { h, mount, typing } from "./ui/dom";
import { lamp } from "./ui/widgets";
import { mountSignin } from "./views/signin";
import { mountWeek } from "./views/week";
import { mountDeals } from "./views/deals";
import { mountLists } from "./views/lists";
import { mountPeople } from "./views/people";
import { mountSources } from "./views/sources";

const root = document.getElementById("root")!;
const PAGES: { id: PageId; label: string; mount: MountFn }[] = [
  { id: "week", label: "WEEK", mount: mountWeek },
  { id: "deals", label: "DEALS", mount: mountDeals },
  { id: "lists", label: "LISTS", mount: mountLists },
  { id: "people", label: "PEOPLE", mount: mountPeople },
  { id: "sources", label: "SOURCES", mount: mountSources },
];

/* ---------- theme ---------- */
function getTheme(): "day" | "night" {
  try {
    const t = localStorage.getItem("radar.theme");
    if (t === "day" || t === "night") return t;
  } catch {
    /* storage blocked */
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "day" : "night";
}
function setTheme(t: "day" | "night", persist = true): void {
  document.documentElement.setAttribute("data-theme", t);
  if (persist) {
    try {
      localStorage.setItem("radar.theme", t);
    } catch {
      /* storage blocked */
    }
  }
}
setTheme(getTheme(), false);

/* ---------- toast ---------- */
let toastBox: HTMLElement | null = null;
function toast(message: string, tone: "ok" | "warn" | "err" = "ok"): void {
  if (!toastBox) {
    toastBox = h("div", { class: "toasts", role: "status" });
    document.body.appendChild(toastBox);
  }
  const t = h("div", { class: "toast " + (tone === "ok" ? "" : tone) }, message);
  toastBox.appendChild(t);
  setTimeout(() => t.remove(), tone === "err" ? 6000 : 3000);
}

/* ---------- routing ---------- */
function parseHash(): { page: PageId; params: Record<string, string> } {
  const parts = location.hash.replace(/^#\/?/, "").split("/");
  const page = (PAGES.find((p) => p.id === parts[0])?.id ?? "week") as PageId;
  const params: Record<string, string> = {};
  if (parts[1]) {
    try {
      const v = decodeURIComponent(parts.slice(1).join("/"));
      if (page === "deals") params.id = v;
      else if (page === "people") params.k = v;
    } catch {
      /* bad escape */
    }
  }
  return { page, params };
}
function hashFor(page: PageId, params?: Record<string, string>): string {
  const extra = params?.id ?? params?.k;
  return "#/" + page + (extra ? "/" + encodeURIComponent(extra) : "");
}

let sessionHandler: (() => void) | null = null;

/* ---------- boot ---------- */
async function boot(): Promise<void> {
  let session: Session | null;
  try {
    session = await api.session();
  } catch (e) {
    mount(root, h("div", { class: "signin" }, h("div", { class: "panel" }, h("div", { class: "micro" }, "NO LINK"), h("p", { class: "rd mono" }, (e as Error).message || "Can't reach the server."))));
    return;
  }
  if (!session) {
    mountSignin(root, () => void boot());
    return;
  }
  const [network, state] = await Promise.all([api.getNetwork(), api.getState()]);
  startApp(session, network, { notes: state.notes ?? {}, responses: state.responses ?? {}, contacted: state.contacted ?? {}, deals: state.deals ?? [] });
}

function startApp(session0: Session, network0: Network | null, state0: PrivateState): void {
  let session = session0;
  let byK: Map<string, Network["contacts"][number]> | null = null;
  let byKFor: Network | null = null;
  const subs = new Set<() => void>();
  const emit = () => {
    for (const f of [...subs]) {
      try {
        f();
      } catch (e) {
        console.error(e);
      }
    }
    paintTop();
  };
  const contactOf = (k: string) => {
    if (!ctx.network) return undefined;
    if (byKFor !== ctx.network) {
      byK = new Map(ctx.network.contacts.map((c) => [c.k, c]));
      byKFor = ctx.network;
    }
    return byK!.get(k);
  };

  /** Applies a change locally, tells the server, and rolls back with a toast if the server refuses. */
  async function optimistic(apply: () => () => void, send: () => Promise<unknown>): Promise<void> {
    const undo = apply();
    emit();
    try {
      await send();
    } catch (e) {
      undo();
      emit();
      toast((e as Error).message || "Couldn't save that.", "err");
    }
  }

  const ctx: AppContext = {
    session,
    network: network0,
    state: state0,
    asOf: todayIso(),
    api,
    setNote(k, note) {
      return optimistic(
        () => {
          const prev = ctx.state.notes[k];
          if (note) ctx.state.notes[k] = { ...note, updatedAt: new Date().toISOString() };
          else delete ctx.state.notes[k];
          return () => {
            if (prev) ctx.state.notes[k] = prev;
            else delete ctx.state.notes[k];
          };
        },
        () => api.putNote(k, note),
      );
    },
    setResponse(dealId, k, response: DealResponse | null) {
      return optimistic(
        () => {
          const m = (ctx.state.responses[dealId] ??= {});
          const prev = m[k];
          if (response) m[k] = response;
          else delete m[k];
          return () => {
            if (prev) m[k] = prev;
            else delete m[k];
          };
        },
        () => api.putResponse(dealId, k, response),
      );
    },
    markContacted(k, date) {
      return optimistic(
        () => {
          const prev = ctx.state.contacted[k];
          if (date) ctx.state.contacted[k] = date;
          else delete ctx.state.contacted[k];
          return () => {
            if (prev) ctx.state.contacted[k] = prev;
            else delete ctx.state.contacted[k];
          };
        },
        () => api.putContacted(k, date),
      );
    },
    saveDeal(deal: Deal) {
      return optimistic(
        () => {
          const prev = ctx.state.deals;
          const i = prev.findIndex((d) => d.deal_id === deal.deal_id);
          ctx.state.deals = i >= 0 ? prev.map((d, j) => (j === i ? deal : d)) : [...prev, deal];
          return () => {
            ctx.state.deals = prev;
          };
        },
        () => api.putDeal(deal),
      );
    },
    removeDeal(id) {
      return optimistic(
        () => {
          const prev = ctx.state.deals;
          const prevResp = ctx.state.responses[id];
          ctx.state.deals = prev.filter((d) => d.deal_id !== id);
          delete ctx.state.responses[id];
          return () => {
            ctx.state.deals = prev;
            if (prevResp) ctx.state.responses[id] = prevResp;
          };
        },
        () => api.deleteDeal(id),
      );
    },
    async setNetwork(net) {
      ctx.network = net;
      try {
        session = (await api.session()) ?? session;
        ctx.session = session;
      } catch {
        /* keep the old session */
      }
      emit();
    },
    holds(k): HoldInfo {
      const note: PrivateNote | undefined = ctx.state.notes[k];
      const c = contactOf(k);
      let auto = null;
      if (c && ctx.network) auto = holdReason(c, stateAsOf(c, ctx.asOf, ctx.network, { contacted: ctx.state.contacted }), ctx.asOf, null);
      return { hold: !!note?.hold || !!auto, noPitch: !!note?.noPitch, note: note?.note ?? "", auto };
    },
    get ai() {
      return session.claude ? api.ai : null;
    },
    toast,
    save(filename, blob) {
      const url = URL.createObjectURL(blob);
      const a = h("a", { href: url, download: filename });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    navigate(page, params) {
      const next = hashFor(page, params);
      if (location.hash === next) route();
      else location.hash = next;
    },
    onChange(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };

  const onSession = async () => {
    try {
      session = (await api.session()) ?? session;
      ctx.session = session;
      emit();
    } catch {
      /* keep the old session */
    }
  };
  sessionHandler && document.removeEventListener("radar:session", sessionHandler);
  sessionHandler = onSession;
  document.addEventListener("radar:session", onSession);

  /* ---------- shell ---------- */
  const annun = h("div", { class: "annun" });
  const keys = h("nav", { class: "pagekeys", "aria-label": "Pages" });
  const bottom = h("nav", { class: "bottombar", "aria-label": "Pages" });
  const dayBtn = h("button", { type: "button" }, "DAY");
  const nightBtn = h("button", { type: "button" }, "NIGHT");
  const paintTheme = () => {
    const t = document.documentElement.getAttribute("data-theme");
    dayBtn.className = t === "day" ? "on" : "";
    nightBtn.className = t === "night" ? "on" : "";
    dayBtn.setAttribute("aria-pressed", String(t === "day"));
    nightBtn.setAttribute("aria-pressed", String(t === "night"));
  };
  dayBtn.addEventListener("click", () => (setTheme("day"), paintTheme()));
  nightBtn.addEventListener("click", () => (setTheme("night"), paintTheme()));
  const signOut = h("button", { class: "btn sm signout", type: "button" }, "SIGN OUT");
  signOut.addEventListener("click", async () => {
    try {
      await api.signOut();
    } catch {
      /* already out */
    }
    location.hash = "";
    cleanup?.();
    cleanup = undefined;
    void boot();
  });
  const sessionLabel = h("span", null, session.label || session.workspace);
  const top = h(
    "header",
    { class: "top" },
    h("div", { class: "brand" }, h("b", null, "RADAR"), sessionLabel),
    keys,
    h("div", { class: "grow" }),
    annun,
    h("div", { class: "dn", role: "group", "aria-label": "Theme" }, dayBtn, nightBtn),
    signOut,
  );
  const main = h("main", { id: "page" });
  mount(root, top, main, bottom);
  paintTheme();

  PAGES.forEach((p, i) => {
    keys.appendChild(h("a", { href: "#/" + p.id, "data-page": p.id }, h("i", null, String(i + 1)), p.label));
    bottom.appendChild(h("a", { href: "#/" + p.id, "data-page": p.id }, h("i", null, String(i + 1)), p.label));
  });

  function paintTop(): void {
    const net = ctx.network;
    const s = ctx.session;
    const build = net?.generated ? net.generated.slice(5, 10) : "--";
    mount(
      annun,
      lamp("VAULT", !!net, "green", net ? `${net.contacts.length} contacts loaded` : "No network loaded"),
      lamp("MONDAY", !!s.monday?.connected, "green", s.monday?.account ?? "Monday not connected"),
      lamp("CLAUDE", !!s.claude, "cyan", s.claude ? "Claude is linked" : "No API key"),
      Object.assign(lamp("BUILD " + build, !!net, "amber", "Radar built as of " + (net?.generated ?? "never")), {}),
    );
    annun.lastElementChild?.classList.add("opt");
    annun.children[2]?.classList.add("opt");
  }
  paintTop();

  /* ---------- router ---------- */
  let cleanup: (() => void) | undefined;
  let current: { page: PageId; el: HTMLElement } | null = null;
  function route(): void {
    const { page, params } = parseHash();
    for (const a of document.querySelectorAll<HTMLElement>("[data-page]")) a.classList.toggle("on", a.dataset.page === page);
    if (current && current.page === page && page !== "week") {
      current.el.dispatchEvent(new CustomEvent("params", { detail: params }));
      return;
    }
    if (cleanup) {
      try {
        cleanup();
      } catch (e) {
        console.error(e);
      }
      cleanup = undefined;
    }
    const el = h("div", { class: "page-" + page });
    mount(main, el);
    current = { page, el };
    window.scrollTo(0, 0);
    try {
      const out = PAGES.find((p) => p.id === page)!.mount(el, ctx, params);
      if (typeof out === "function") cleanup = out;
    } catch (e) {
      console.error(e);
      mount(el, h("div", { class: "panel" }, h("div", { class: "micro" }, "FAULT"), h("p", { class: "rd mono" }, String((e as Error).message || e))));
    }
  }
  window.onhashchange = route;
  if (!location.hash) location.hash = "#/week";
  else route();

  /* ---------- keys ---------- */
  document.onkeydown = (e) => {
    if (e.key === "Escape") {
      document.dispatchEvent(new CustomEvent("radar:escape"));
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
    const n = Number(e.key);
    if (n >= 1 && n <= PAGES.length && e.key.length === 1) {
      e.preventDefault();
      ctx.navigate(PAGES[n - 1].id);
    } else if (e.key === "/") {
      e.preventDefault();
      ctx.navigate("people");
      setTimeout(() => document.getElementById("people-search")?.focus(), 30);
    }
  };
}

void boot();
